import type { WorkoutExerciseReplacement } from '@/lib/fitness/replacement';
import { MUSCLE_GROUP_LIST, type MuscleGroupKey } from '@/lib/exerciseLibrary';
import { getFreeWorkoutExerciseChoices } from '@/lib/fitness/freeWorkoutSelection';
import { freeWorkoutTimeDefault, getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { fitnessExercisePresentationName } from '@/lib/fitnessExercisePresentation';
import { getExerciseAvatarDemonstration } from '@/lib/fitnessDemonstration';

export type ReplacementEquipment = 'bodyweight' | 'barbell' | 'dumbbell' | 'cable' | 'machine' | 'kettlebell' | 'ezbar' | 'bench' | 'pullup' | 'parallel' | 'abwheel' | 'jumpRope' | 'battleRopes' | 'cardioMachine' | 'box' | 'landmine' | 'other';
export interface WorkoutReplacementChoice {
  id: string; name: string; muscle: MuscleGroupKey; timed: boolean; defaultReps: number;
  defaultWeight: 0; restSeconds: number; primaryEquipment: ReplacementEquipment;
  movementId: string;
}

// Primary apparatus from the authored descriptor, not translated labels or name substrings.
// This filter does not promise that every required bench/rack/support is available.
const byKind: Readonly<Record<string, ReplacementEquipment>> = {
  barbell: 'barbell', dumbbells: 'dumbbell', 'shared-dumbbell': 'dumbbell',
  cable: 'cable', 'ankle-cable': 'cable', 'cable-straight-bar': 'cable', 'dual-cable': 'cable', 'low-cable-row': 'cable',
  'chest-press-machine': 'machine', 'hack-squat': 'machine', 'hip-abduction': 'machine', 'lat-pulldown': 'machine',
  'leg-curl': 'machine', 'leg-extension': 'machine', 'leg-press': 'machine', 'pec-deck': 'machine', 'seated-calf': 'machine',
  kettlebell: 'kettlebell', 'preacher-ez-bar': 'ezbar', bench: 'bench', 'pull-up': 'pullup', 'weighted-pull-up': 'pullup',
  'parallel-bars': 'parallel', 'ab-wheel': 'abwheel', 'jump-rope': 'jumpRope', 'battle-ropes': 'battleRopes',
  elliptical: 'cardioMachine', 'rowing-machine': 'cardioMachine', 'stair-climber': 'cardioMachine',
  'stationary-bike': 'cardioMachine', treadmill: 'cardioMachine',
  box: 'box', 'landmine-row': 'landmine', 'locomotion-surface': 'bodyweight',
};
const muscleGroups: Readonly<Record<string, MuscleGroupKey>> = {
  chest: 'chest', pectorals: 'chest', back: 'back', lats: 'back', spine: 'back', 'upper back': 'back',
  shoulders: 'shoulders', delts: 'shoulders', deltoids: 'shoulders', 'rear deltoids': 'shoulders',
  biceps: 'biceps', triceps: 'triceps', legs: 'legs', quads: 'legs', hamstrings: 'legs', calves: 'legs',
  'upper legs': 'legs', 'lower legs': 'legs', core: 'core', abs: 'core', waist: 'core', glutes: 'glutes',
  cardio: 'cardio', 'cardiovascular system': 'cardio',
};
const allChoices: readonly WorkoutReplacementChoice[] = MUSCLE_GROUP_LIST.flatMap(group =>
  getFreeWorkoutExerciseChoices(group).flatMap(exercise => {
    const movement = getExplicitFitnessMovement(exercise.i18nKey);
    if (!movement) return [];
    const duration = freeWorkoutTimeDefault(exercise.i18nKey);
    const demonstration = getExerciseAvatarDemonstration(movement.canonicalName);
    const primaryEquipment = demonstration ? demonstration.equipment ? byKind[demonstration.equipment.kind] ?? 'other' : 'bodyweight' : 'other';
    return [{
      id: exercise.id, name: exercise.i18nKey, muscle: group, timed: movement.unit === 'seconds',
      defaultReps: duration ?? 10, defaultWeight: 0 as const, restSeconds: 60,
      primaryEquipment, movementId: movement.id,
    }];
  }),
);

const currentMovement = (name: string, muscle: string) =>
  getExplicitFitnessMovement(fitnessExercisePresentationName(name, muscle));

export function workoutReplacementGroup(currentName: string, muscle: string): MuscleGroupKey | null {
  const exact = muscleGroups[muscle.trim().toLowerCase()];
  if (exact) return exact;
  const movement = currentMovement(currentName, muscle);
  return movement ? allChoices.find(choice => choice.movementId === movement.id)?.muscle ?? null : null;
}

/** Curated alternatives only. Groups/equipment narrow discovery; they do not claim clinical equivalence. */
export function workoutReplacementChoices(currentName: string, muscle: string, group: MuscleGroupKey | 'all', equipment: ReplacementEquipment | 'all'): WorkoutReplacementChoice[] {
  const movement = currentMovement(currentName, muscle);
  return allChoices.filter(choice => choice.movementId !== movement?.id
    && (group === 'all' || choice.muscle === group)
    && (equipment === 'all' || choice.primaryEquipment === equipment));
}

/** Strict runtime payload: discovery IDs and equipment metadata never cross the controller boundary. */
export function workoutReplacementPayload(choice: WorkoutReplacementChoice): WorkoutExerciseReplacement {
  const { name, muscle, timed, defaultReps, defaultWeight, restSeconds } = choice;
  return { name, muscle, timed, defaultReps, defaultWeight, restSeconds };
}
